/**
 * OFL GA4 Analytics — Custom Event Tracking
 *
 * GA4 base tracking is provided by Shopify's native Google channel (G-T5BWXPJ73D).
 * This file adds ONLY custom events not covered by that channel:
 *
 *   - Scroll depth (25 / 50 / 75 / 90 %)
 *   - CTA / button clicks (with label + destination)
 *   - Subscription funnel: view_offer, select_billing, select_platform,
 *     checkout_intent (paid buy clicks only, exactly once), freemium_click
 *   - Newsletter / email signup submissions
 *
 * FUNNEL CONTRACT (docs/cro/proposals.md P-1). Sections that track their own
 * funnel (ofl-pricing-tiers, ofl-paid-landing, ofl-nt8-landing,
 * ofl-comparison-landing) carry data-ofl-tracks-checkout on their root and
 * this file stays out of their way. Sections that don't (ofl-platform-checkout,
 * ofl-upgrade-checkout) are tracked here from data attributes on the root:
 *   data-ofl-tier, data-ofl-platform, data-ofl-value-quarterly/-monthly,
 *   data-channel, data-billing (live state).
 * Any price card marked data-ofl-offer fires view_offer once per page view.
 *
 * NOTE: Standard ecommerce events (add_to_cart, purchase, view_item) are
 * handled automatically by the Shopify Google channel — do not duplicate here.
 */

(function () {
  if (typeof gtag !== 'function') return;

  /* ─── Scroll Depth ───────────────────────────────────────────── */
  var scrollThresholds = [25, 50, 75, 90];
  var scrollFired = {};

  function getScrollPercent() {
    var body = document.body;
    var html = document.documentElement;
    var scrollTop = window.scrollY || html.scrollTop;
    var scrollHeight = Math.max(
      body.scrollHeight, html.scrollHeight,
      body.offsetHeight, html.offsetHeight,
      body.clientHeight, html.clientHeight
    ) - html.clientHeight;
    if (scrollHeight <= 0) return 100;
    return Math.round((scrollTop / scrollHeight) * 100);
  }

  window.addEventListener('scroll', function () {
    var pct = getScrollPercent();
    scrollThresholds.forEach(function (threshold) {
      if (!scrollFired[threshold] && pct >= threshold) {
        scrollFired[threshold] = true;
        gtag('event', 'scroll_depth', {
          percent_scrolled: threshold,
          page_path: window.location.pathname
        });
      }
    });
  }, { passive: true });

  /* ─── CTA / Button Clicks ────────────────────────────────────── */
  document.addEventListener('click', function (e) {
    var el = e.target.closest(
      '.button--primary, .button--secondary, .ofl-pricing__cta, ' +
      '.ofl-lc-cta a, [data-ga4-cta], [data-upgrade-cta]'
    );
    if (!el) return;

    var label = (el.innerText || el.textContent || '').trim().slice(0, 100);
    var href  = el.getAttribute('href') || '';
    var isPricingCta = el.classList.contains('ofl-pricing__cta');

    gtag('event', 'cta_click', {
      cta_label: label,
      cta_destination: href,
      page_path: window.location.pathname
    });

    /* Free-tier CTA: never a checkout. (It used to be filed as
       checkout_intent because it shares the pricing CTA class.) */
    if (el.hasAttribute('data-ofl-free-cta') || (isPricingCta && el.tagName === 'A' && /freemium/.test(href))) {
      var freeRoot = oflFunnelRoot(el);
      gtag('event', 'freemium_click', {
        cta_location: 'pricing_card',
        channel: (freeRoot && freeRoot.getAttribute('data-channel')) || '',
        page_path: window.location.pathname
      });
      return;
    }

    /* Subscription funnel — paid buy click. Self-tracking sections fire
       their own, richer checkout_intent; firing here too double-counted. */
    var isBuy = (isPricingCta && el.tagName !== 'A') || el.hasAttribute('data-upgrade-cta');
    if (isBuy && !el.closest('[data-ofl-tracks-checkout]')) {
      /* checkout_intent, NOT begin_checkout: the Shopify Google channel
         fires the real begin_checkout on the checkout page. Reusing that
         name here would blur the funnel stage and inflate the secondary
         begin_checkout conversion action imported in Google Ads. */
      var p = oflFunnelParams(el, { currency: 'USD' });
      p.items = [{
        item_name: 'OFL ' + (p.tier || 'suite') + (p.platform ? ' for ' + p.platform : ''),
        item_category: 'subscription',
        item_variant: p.billing || ''
      }];
      gtag('event', 'checkout_intent', p);
    }
  }, true); /* capture: read label/state before section handlers swap the text to "Loading…" */

  /* ─── Offer-modal tiles ──────────────────────────────────────── */
  /* The sitewide offer popup sells through plain links to
     /pages/go?p={platform}&t={quarter|month}; ofl-go-redirect then posts to
     checkout without firing anything, so the buy click is recorded here. */
  document.addEventListener('click', function (e) {
    var tile = e.target.closest('a[href*="/pages/go?"]');
    if (!tile) return;
    var q;
    try { q = new URL(tile.href, window.location.origin).searchParams; } catch (err) { return; }
    var platform = (q.get('p') || '').toLowerCase();
    var priceEl = tile.querySelector('.ofl-price-num');
    var value = priceEl ? parseFloat(priceEl.textContent.replace(/[^0-9.]/g, '')) : NaN;
    var params = {
      currency: 'USD',
      tier: platform === 'discord' ? 'discord' : 'suite',
      platform: platform === 'discord' ? '' : platform,
      billing: q.get('t') === 'month' ? 'monthly' : 'quarterly',
      channel: 'offer_modal',
      page_path: window.location.pathname
    };
    if (!isNaN(value)) params.value = value;
    gtag('event', 'checkout_intent', params);
  }, true);

  /* ─── Funnel helpers + delegated toggles (non-self-tracking sections) ── */
  function oflFunnelRoot(el) {
    return el.closest('[data-ofl-tracks-checkout], [data-ofl-tier]') || el.closest('.ofl-pricing, [data-billing]');
  }

  function oflFunnelParams(el, extra) {
    var root = oflFunnelRoot(el);
    var out = extra || {};
    out.page_path = window.location.pathname;
    if (!root) return out;
    var billing = root.getAttribute('data-billing') || '';
    var platformIds = { ninjatrader: 'ninja' }; /* one id per platform across surfaces */
    var picked = root.querySelector('[data-platform][aria-pressed="true"], [data-nt8-platform][aria-pressed="true"]');
    var value = parseFloat(root.getAttribute('data-ofl-value-' + billing));
    out.tier = root.getAttribute('data-ofl-tier') || 'suite';
    out.platform = (picked && (picked.getAttribute('data-platform') || picked.getAttribute('data-nt8-platform'))) || root.getAttribute('data-ofl-platform') || '';
    out.platform = platformIds[out.platform] || out.platform;
    out.billing = billing;
    out.channel = root.getAttribute('data-channel') || '';
    if (!isNaN(value)) out.value = value;
    return out;
  }

  document.addEventListener('click', function (e) {
    var el = e.target.closest('[data-billing-option], .ofl-upgrade__plat-btn');
    if (!el || el.closest('[data-ofl-tracks-checkout]')) return;
    /* The section's own listener (registered earlier, on the element) has
       already flipped data-billing / aria-pressed by the time this runs. */
    if (el.hasAttribute('data-billing-option')) {
      gtag('event', 'select_billing', oflFunnelParams(el));
    } else {
      gtag('event', 'select_platform', oflFunnelParams(el));
    }
  });

  /* ─── Offer seen ─────────────────────────────────────────────── */
  /* view_offer: the price card was at least half visible for a full second.
     Once per card per page view. Tells "never reached the price" apart from
     "saw the price and left". */
  if ('IntersectionObserver' in window) {
    var offerTimers = new WeakMap();
    var offerObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var card = entry.target;
        if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
          if (offerTimers.has(card)) return;
          offerTimers.set(card, setTimeout(function () {
            offerObserver.unobserve(card);
            var vp = oflFunnelParams(card, { offer: card.getAttribute('data-ofl-offer') || 'suite' });
            if (vp.offer === 'discord') { vp.tier = 'discord'; vp.platform = ''; delete vp.value; }
            gtag('event', 'view_offer', vp);
          }, 1000));
        } else if (offerTimers.has(card)) {
          clearTimeout(offerTimers.get(card));
          offerTimers.delete(card);
        }
      });
    }, { threshold: [0, 0.5] });
    document.querySelectorAll('[data-ofl-offer]').forEach(function (card) { offerObserver.observe(card); });
  }

  /* ─── Newsletter Form Submission ────────────────────────────── */
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (
      form.id === 'ContactFooter' ||
      form.classList.contains('newsletter-form') ||
      form.action && form.action.indexOf('contact') !== -1
    ) {
      gtag('event', 'newsletter_signup', {
        page_path: window.location.pathname
      });
    }
  });

})();
